import React, { useRef, useState, useMemo } from "react";
import { useSchools } from "../../hooks/useSchools";
import { useSessionClass } from "../../contexts/SessionClassContext";
import {
    Box,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Grid,
    Button,
    Paper,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { GosvaraResponseDto } from "../../hooks/useGosvaraSheet";
import { useTheme } from "@mui/material/styles";
import { flushSync } from "react-dom";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STAT_COLUMNS = ["REGISTERED", "APPEARED", "ABSENT", "PASS", "FAIL", "PASS%"];






const CATEGORIES = ["SC", "ST", "OBC", "GEN", "TOTAL"] as const;
const GENDERS = ["M", "F", "Total"] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
    schoolId: number;
    classId: number;
    marksheetType: string;
    monthType: string;
    /** Map returned by POST /gosvara/category-wise. Keys: SC, ST, OBC, GEN, ALL */
    categoryData?: Record<string, GosvaraResponseDto>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map UI category label → backend response key */
const getCatKey = (cat: string): string =>
    cat === "TOTAL" ? "ALL" : cat === "GEN" ? "GEN" : cat;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const GosvaraCategoryTemplate: React.FC<Props> = ({
    schoolId,
    classId,
    marksheetType,
    monthType,
    categoryData,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isDownloading, setIsDownloading] = useState(false);
    const templateRef = useRef<HTMLDivElement>(null);

    const { useSchoolById } = useSchools();
    const { selectedSession, selectedClass } = useSessionClass();
    const { data: schoolData } = useSchoolById(schoolId);

    const classNumber = useMemo(() => {
        const name = selectedClass?.name ?? "";
        const n = parseInt(String(name).match(/\d+/)?.[0] ?? "", 10);
        return Number.isFinite(n) ? n : null;
    }, [selectedClass?.name]);

    const GRADE_COLUMNS = useMemo(() => {
        const plusGradeClasses = new Set([3, 4, 6, 7]);
        if (classNumber && plusGradeClasses.has(classNumber)) {
            return [
                { label: "A+", range: "(86 - 100)" },
                { label: "A", range: "(76 - 85)" },
                { label: "B+", range: "(66 - 75)" },
                { label: "B", range: "(56 - 65)" },
                { label: "C+", range: "(51 - 55)" },
                { label: "C", range: "(46 - 50)" },
                { label: "D", range: "(33 - 45)" },
                { label: "E", range: "(0 - 32)" },
            ];
        }
        return [
            { label: "A+", range: "(85 - 100)" },
            { label: "A", range: "(75 - 84)" },
            { label: "B", range: "(60 - 74)" },
            { label: "C", range: "(45 - 59)" },
            { label: "D", range: "(33 - 44)" },
            { label: "E1", range: "(20 - 32)" },
            { label: "E2", range: "(0 - 19)" },
        ];
    }, [classNumber]);

    // ── Loading guard ───────────────────────────────────────────────────────
    if (!categoryData) {
        return (
            <Box textAlign="center" p={4}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t("loading_data")}</Typography>
            </Box>
        );
    }

    // ── Download ────────────────────────────────────────────────────────────
    const downloadAsImage = async () => {
        if (!templateRef.current) return;
        flushSync(() => setIsDownloading(true));
        try {
            // Ensure React has painted the "export mode" DOM before snapshot.
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

            const canvas = await html2canvas(templateRef.current, {
                useCORS: true,
                allowTaint: true,
                background: "#ffffff",
                logging: false,
                scale: 2,
            } as any);
            const link = document.createElement("a");
            link.download = `gosvara-category-${selectedClass?.name || "class"}-${marksheetType}-${monthType}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
        } catch (_) {
            // silent
        } finally {
            setIsDownloading(false);
        }
    };

    // ── categoryStudentCount is identical across all DTOs; pick from ALL/first ──
    const anyDto = categoryData["ALL"] ?? categoryData["SC"] ?? Object.values(categoryData)[0];
    const countMap = anyDto?.categoryStudentCount ?? {};

    /**
     * Get the student count for (UI category, gender) from categoryStudentCount.
     * countMap keys: GEN, SC, ST, OBC, ALL   sub-keys: MALE, FEMALE, TOTAL
     */
    const getCount = (cat: string, gender: string): number => {
        const counts = countMap[getCatKey(cat)];
        if (!counts) return 0;
        if (gender === "M") return (counts["MALE"] as number) ?? 0;
        if (gender === "F") return (counts["FEMALE"] as number) ?? 0;
        if (gender === "Total") return (counts["TOTAL"] as number) ?? 0;
        return 0;
    };

    /**
     * Derive gender-wise numbers when backend doesn't send appearedMale/absentFemale/etc.
     * We allocate the TOTAL stat across M/F proportionally by registered counts,
     * ensuring M + F === TOTAL (integer-safe).
     */
    const splitByRegistered = (
        total: number,
        registeredMale: number,
        registeredFemale: number
    ): { male: number; female: number } => {
        const denom = (registeredMale ?? 0) + (registeredFemale ?? 0);
        if (!denom || !Number.isFinite(total)) return { male: 0, female: 0 };
        const male = Math.floor((total * (registeredMale ?? 0)) / denom);
        const female = Math.max(0, (total ?? 0) - male);
        return { male, female };
    };

    /**
     * Get the display value for one table cell.
     *  - Grade columns  → from dto.overallGradeSummary (total row; M/F from maleGradeSummary/femaleGradeSummary)
     *  - REGISTERED M/F → from categoryStudentCount
     *  - Stat totals    → from subjectResults "TOTAL" row
     */
    const getValue = (cat: string, gender: string, col: string): string | number => {
        const dto = categoryData[getCatKey(cat)] ?? categoryData[cat];
        if (!dto) return 0;

        // Grade columns
        if (GRADE_COLUMNS.some((g) => g.label === col)) {
            if (gender === "Total") return dto.overallGradeSummary?.[col] ?? 0;
            if (gender === "M") return dto.maleGradeSummary?.[col] ?? 0;
            if (gender === "F") return dto.femaleGradeSummary?.[col] ?? 0;
            return 0;
        }

        // Stat columns — TOTAL subject row is the aggregate
        const stats: any =
            dto.subjectResults?.find((r: any) => r.subject === "TOTAL") ??
            dto.subjectResults?.[0] ??
            {};

        const regM = getCount(cat, "M");
        const regF = getCount(cat, "F");
        const regT = getCount(cat, "Total");

        switch (col) {
            case "REGISTERED":
                if (gender === "M") return stats.registeredMale ?? getCount(cat, "M");
                if (gender === "F") return stats.registeredFemale ?? getCount(cat, "F");
                return stats.registered ?? getCount(cat, "Total");

            case "APPEARED":
                if (gender === "Total") return stats.appeared ?? 0;
                if (gender === "M") return stats.appearedMale ?? splitByRegistered(stats.appeared ?? 0, regM, regF).male;
                if (gender === "F") return stats.appearedFemale ?? splitByRegistered(stats.appeared ?? 0, regM, regF).female;
                return 0;

            case "ABSENT":
                if (gender === "Total") return stats.absent ?? 0;
                if (gender === "M") return stats.absentMale ?? splitByRegistered(stats.absent ?? 0, regM, regF).male;
                if (gender === "F") return stats.absentFemale ?? splitByRegistered(stats.absent ?? 0, regM, regF).female;
                return 0;

            case "PASS":
                if (gender === "Total") return stats.passed ?? 0;
                if (gender === "M") return stats.passedMale ?? splitByRegistered(stats.passed ?? 0, regM, regF).male;
                if (gender === "F") return stats.passedFemale ?? splitByRegistered(stats.passed ?? 0, regM, regF).female;
                return 0;

            case "FAIL":
                if (gender === "Total") return stats.failed ?? 0;
                if (gender === "M") return stats.failedMale ?? splitByRegistered(stats.failed ?? 0, regM, regF).male;
                if (gender === "F") return stats.failedFemale ?? splitByRegistered(stats.failed ?? 0, regM, regF).female;
                return 0;

            case "PASS%":
                if (gender === "Total") return `${stats.passPercentage ?? 0}%`;
                // Compute gender pass% from derived numbers when not provided by backend.
                // Prefer derived appeared/passed (not registered) to match backend meaning.
                if (gender === "M") {
                    const appearedM = stats.appearedMale ?? splitByRegistered(stats.appeared ?? 0, regM, regF).male;
                    const passedM = stats.passedMale ?? splitByRegistered(stats.passed ?? 0, regM, regF).male;
                    return appearedM > 0 ? `${Math.round((passedM / appearedM) * 100)}%` : "0%";
                }
                if (gender === "F") {
                    const appearedF = stats.appearedFemale ?? splitByRegistered(stats.appeared ?? 0, regM, regF).female;
                    const passedF = stats.passedFemale ?? splitByRegistered(stats.passed ?? 0, regM, regF).female;
                    return appearedF > 0 ? `${Math.round((passedF / appearedF) * 100)}%` : "0%";
                }
                // Fallback: if a different gender label is introduced later.
                return regT > 0 ? `${stats.passPercentage ?? 0}%` : "0%";

            default:
                return 0;
        }
    };

    // ── Shared styles ───────────────────────────────────────────────────────
    const headerSx = {
        border: "1px solid #e0e0e0",
        fontWeight: "bold",
        bgcolor: "#e3f2fd",
        color: theme.palette.primary.main,
        fontSize: "0.85rem",
        textAlign: "center" as const,
        padding: "6px 4px",
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <Box sx={{ bgcolor: "#f8fafc", height: "fit-content",pb: "0"}}>
            <div ref={templateRef} style={{ padding: "15px 5px" }}>

                {/* ── School name header ── */}
                <Card sx={{ mb: 2, borderRadius: 2, bgcolor: "#3092e3" }}>
                    <CardContent sx={{ p: 0.5, "&:last-child": { paddingBottom: 0 } }}>
                        <Box sx={{ pl: 2, py: 1 }}>
                            <Typography variant="h4" fontWeight="bold" color="white">
                                {(schoolData?.nameofSchool || "").toUpperCase()}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* ── Info section ── */}
                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                    <CardContent sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        <Typography variant="body2"><b>{t("district")}:</b> {schoolData?.district || "INDORE"}</Typography>
                                        <Typography variant="body2"><b>{t("year")}:</b> {selectedSession?.name || "2025-2026"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        <Typography variant="body2"><b>{t("mpbse")}:</b> {schoolData?.schoolCode || "531062"}</Typography>
                                        <Typography variant="body2"><b>{t("dise")}:</b> {schoolData?.diseCode || "23230119206"}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2"><b>{t("goshwara_type")}:</b> {String(marksheetType ?? "").replace(/_/g, " ").toUpperCase()}</Typography>
                                <Typography variant="body2"><b>{t("month_type")}:</b> {monthType.slice(0, 3).toUpperCase()}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3} textAlign="right">
                                <Box pr={4}>
                                    <Typography variant="body1" fontWeight="bold" color="primary">
                                        {selectedClass?.name} – {selectedClass?.section}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Category-wise table ── */}
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                    <Table size="small" sx={{ borderCollapse: "collapse" }}>
                        <TableHead>
                            <TableRow>
                                {/* Category + Gender label columns */}
                                <TableCell sx={{ ...headerSx, textAlign: "left", pl: 2 }} colSpan={2}>
                                    CATEGORY
                                </TableCell>

                                {/* Grade headers */}
                                {GRADE_COLUMNS.map((g) => (
                                    <TableCell key={g.label} sx={headerSx}>
                                        {g.label}
                                        <br />
                                        <Typography
                                            variant="caption"
                                            sx={{ fontWeight: "normal", color: theme.palette.text.secondary }}
                                        >
                                            {g.range}
                                        </Typography>
                                    </TableCell>
                                ))}

                                {/* Stat headers */}
                                {STAT_COLUMNS.map((s) => (
                                    <TableCell key={s} sx={headerSx}>{s}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {CATEGORIES.map((cat) => (
                                <React.Fragment key={cat}>
                                    {GENDERS.map((gender, gIdx) => {
                                        const isTotalRow = cat === "TOTAL" && gender === "Total";
                                        const isGenderTotal = gender === "Total";
                                        const rowBg = isTotalRow ? "#fff3cd" : isGenderTotal ? "#f9f9f9" : "white";
                                        const textColor = isTotalRow ? "#ff6f00" : theme.palette.text.primary;
                                        const showCategoryTextInDownload = isDownloading ? gIdx === 1 : gIdx === 0;

                                        return (
                                            <TableRow
                                                key={`${cat}-${gender}`}
                                                sx={{ bgcolor: rowBg, "&:hover": { bgcolor: isTotalRow ? "#fff3cd" : "#f0f0f0" } }}
                                            >
                                                {/* Merged category label cell (rowSpan=3) */}
                                                {(!isDownloading ? gIdx === 0 : true) && (
                                                    <TableCell
                                                        rowSpan={isDownloading ? 1 : 3}
                                                        sx={{
                                                            border: "1px solid #e0e0e0",
                                                            fontWeight: "bold",
                                                            bgcolor: cat === "TOTAL" ? "#fff3cd" : "#fafafa",
                                                            color: cat === "TOTAL" ? "#ff6f00" : theme.palette.primary.main,
                                                            fontSize: "1.1rem",
                                                            width: 80,
                                                            textAlign: "center",
                                                            verticalAlign: "middle",
                                                        }}
                                                    >
                                                        {showCategoryTextInDownload ? cat : ""}
                                                    </TableCell>
                                                )}

                                                {/* Gender label (M / F / Total) */}
                                                <TableCell
                                                    sx={{
                                                        border: "1px solid #e0e0e0",
                                                        fontWeight: isGenderTotal ? "bold" : "normal",
                                                        width: 60,
                                                        textAlign: "center",
                                                        fontSize: "17px",
                                                    }}
                                                >
                                                    {gender}
                                                </TableCell>

                                                {/* Grade value cells */}
                                                {GRADE_COLUMNS.map((g) => (
                                                    <TableCell
                                                        key={g.label}
                                                        align="center"
                                                        sx={{
                                                            border: "1px solid #e0e0e0",
                                                            fontWeight: isGenderTotal ? "bold" : "normal",
                                                            color: textColor,
                                                            fontSize: "17px",
                                                        }}
                                                    >
                                                        {getValue(cat, gender, g.label)}
                                                    </TableCell>
                                                ))}

                                                {/* Stat value cells */}
                                                {STAT_COLUMNS.map((stat) => {
                                                    const val = getValue(cat, gender, stat);
                                                    const isPass = stat === "PASS";
                                                    const isFail = stat === "FAIL";
                                                    return (
                                                        <TableCell
                                                            key={stat}
                                                            align="center"
                                                            sx={{
                                                                border: "1px solid #e0e0e0",
                                                                fontWeight: "bold",
                                                                color: isTotalRow
                                                                    ? "#ff6f00"
                                                                    : isPass
                                                                        ? "#4caf50"
                                                                        : isFail
                                                                            ? "#f44336"
                                                                            : textColor,
                                                                bgcolor: isTotalRow ? "#fff3cd" : "inherit",
                                                                fontSize: "17px",
                                                            }}
                                                        >
                                                            {val}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}

                                    {/* Red separator between category groups */}
                                    {cat !== "TOTAL" && (
                                        <TableRow sx={{ height: 2 }}>
                                            <TableCell
                                                colSpan={2 + GRADE_COLUMNS.length + STAT_COLUMNS.length}
                                                sx={{ p: 0, borderBottom: "2px solid #e53935" }}
                                            />
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Download button */}
            <Box sx={{ textAlign: "center", mt: 3, mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={downloadAsImage}
                    disabled={isDownloading}
                    sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: theme.palette.primary.main } }}
                >
                    {isDownloading ? t("generating_image") : t("download_as_image")}
                </Button>
            </Box>
        </Box>
    );
};

export default GosvaraCategoryTemplate;
